/**
 * Iframe JWT Token Communication Utilities
 * Functions to send JWT tokens from react-js parent window to lovable-aligned iframe
 */

import { isIframeOriginAllowed } from '../security/config';

/**
 * Send JWT token to iframe
 * @param iframeRef - Reference to the iframe element
 * @param jwtToken - The JWT token to send
 * @param targetOrigin - The origin of the iframe (use '*' for any origin, or specific origin for security)
 */
export const sendJwtTokenToIframe = (
  iframeRef,
  jwtToken,
  targetOrigin = '*',
) => {
  if (!iframeRef?.contentWindow) {
    console.warn(
      '[sendJwtTokenToIframe] Iframe ref or contentWindow not available',
    );
    return;
  }

  if (!jwtToken) {
    console.warn('[sendJwtTokenToIframe] No JWT token provided');
    return;
  }

  try {
    iframeRef.contentWindow.postMessage(
      {
        type: 'SET_JWT_TOKEN',
        payload: {
          jwtToken,
        },
      },
      targetOrigin,
    );
  } catch (error) {
    console.error('[sendJwtTokenToIframe] Error sending JWT token:', error);
  }
};

/**
 * Clear JWT token in iframe
 * @param iframeRef - Reference to the iframe element
 * @param targetOrigin - The origin of the iframe
 */
export const clearJwtTokenInIframe = (iframeRef, targetOrigin = '*') => {
  if (!iframeRef?.contentWindow) {
    console.warn(
      '[clearJwtTokenInIframe] Iframe ref or contentWindow not available',
    );
    return;
  }

  try {
    iframeRef.contentWindow.postMessage(
      {
        type: 'CLEAR_JWT_TOKEN',
      },
      targetOrigin,
    );
  } catch (error) {
    console.error('[clearJwtTokenInIframe] Error clearing JWT token:', error);
  }
};

/**
 * Send SSO token to iframe
 * @param iframeRef - Reference to the iframe element
 * @param ssoToken - The SSO token to send
 * @param targetOrigin - The origin of the iframe
 */
export const sendSsoTokenToIframe = (
  iframeRef,
  ssoToken,
  targetOrigin = '*',
) => {
  if (!iframeRef?.contentWindow) {
    console.warn(
      '[sendSsoTokenToIframe] Iframe ref or contentWindow not available',
    );
    return;
  }

  if (!ssoToken) {
    console.warn('[sendSsoTokenToIframe] No SSO token provided');
    return;
  }

  try {
    iframeRef.contentWindow.postMessage(
      {
        type: 'SET_SSO_TOKEN',
        payload: {
          ssoToken,
        },
      },
      targetOrigin,
    );
  } catch (error) {
    console.error('[sendSsoTokenToIframe] Error sending SSO token:', error);
  }
};

/**
 * Setup message listener to respond to token requests from iframe
 * @param jwtTokenGetter - Function that returns the current JWT token
 * @param ssoTokenGetter - Function that returns the current SSO token
 * @param targetOrigin - The origin to send messages to
 * @returns Cleanup function to remove the event listener
 */
export const setupIframeTokenListener = (
  jwtTokenGetter,
  ssoTokenGetter = null,
) => {
  const handleMessage = (event) => {
    // Security: Validate origin FIRST - CRITICAL SECURITY CHECK
    if (!isIframeOriginAllowed(event.origin)) {
      console.warn(
        '[Security] Rejected message from unauthorized origin:',
        event.origin,
      );
      return;
    }

    if (event.data?.type === 'REQUEST_JWT_TOKEN') {
      const jwtToken = jwtTokenGetter();
      if (jwtToken && event.source) {
        event.source.postMessage(
          {
            type: 'SET_JWT_TOKEN',
            payload: {
              jwtToken,
            },
          },
          event.origin,
        );
      }
    }

    if (event.data?.type === 'REQUEST_SSO_TOKEN') {
      const ssoToken = ssoTokenGetter?.();
      if (ssoToken && event.source) {
        event.source.postMessage(
          {
            type: 'SET_SSO_TOKEN',
            payload: {
              ssoToken,
            },
          },
          event.origin,
        );
      }
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};
